// One spy's card in the Missions tab: who it is, what it watches, what it
// brought back, and the orders it can be given. Split out of `MissionsTab`
// to keep both under the 400-line mark.

import type { PlayerHudEntry } from '@aod/ui';
import { SceneImage } from '@aod/ui/components/ui/scene';
import type { Espionage, Spy } from '../../espionage/espionage.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import type { OpsActions } from '../../sim/useSimWorld/useEspionageIntents.js';
import { sceneUrls } from '../scenes.js';
import { CounterControls } from './CounterControls.js';
import { PoisonControls } from './PoisonControls.js';
import { RoyalHitControls } from './RoyalHitControls.js';
import { SabotageControls } from './SabotageControls.js';
import { IntelReport, personaOf } from './SpyIntelReport.js';

export interface SpyCardProps {
  readonly spy: Spy;
  readonly espionage: Espionage | null | undefined;
  readonly players: readonly PlayerHudEntry[];
  readonly localPlayerIndex: number;
  readonly nameOf: (royalId: number) => string;
  readonly onDispatchSpy?: (unitId: string, targetCityId: string) => boolean;
  readonly opsActions?: OpsActions;
}

export function SpyCard({
  spy,
  espionage,
  players,
  localPlayerIndex,
  nameOf,
  onDispatchSpy,
  opsActions,
}: SpyCardProps) {
  const t = useT();
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border/50 bg-card/60 px-2.5 py-2">
      <div className="flex items-center gap-2 text-xs">
        <SceneImage
          {...sceneUrls('spyPersona', personaOf(spy.unitId))}
          widths={[128, 512]}
          sizes="32px"
          aspect="square"
          scrim={false}
          className="size-8 shrink-0"
        />
        <span className="min-w-0 flex-1 truncate font-medium text-foreground">
          {t('espionage.endurance', { n: spy.endurance })}
        </span>
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {spy.q},{spy.r}
        </span>
      </div>
      {espionage?.ops && opsActions && (
        <RoyalHitControls
          ops={espionage.ops}
          spy={spy}
          players={players}
          localPlayerIndex={localPlayerIndex}
          nameOf={nameOf}
          onAssassinate={opsActions.assassinate}
        />
      )}
      {espionage?.counter && opsActions && (
        <CounterControls rules={espionage.counter} spy={spy} actions={opsActions} />
      )}
      {spy.post ? (
        <div className="flex flex-col gap-0.5" data-testid={`spy-post-${spy.unitId}`}>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('espionage.watching')}
          </span>
          <span className="text-xs text-foreground">{spy.post.name}</span>
          <IntelReport unitId={spy.unitId} intel={spy.intel} />
          {spy.intel && (
            <SceneImage
              {...sceneUrls('spyAct', 'act-intel-success')}
              widths={[384, 768]}
              sizes="260px"
              aspect="strip"
              data-testid={`spy-intel-art-${spy.unitId}`}
            />
          )}
        </div>
      ) : (
        spy.targets.length > 0 && (
          <>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {t('espionage.targets')}
            </span>
            <div className="flex flex-col gap-0.5">
              {spy.targets.map((target) => (
                <div key={target.cityId} className="flex flex-col gap-1 text-xs">
                  <span className="min-w-0 truncate">{target.name}</span>
                  <div className="flex flex-wrap items-start gap-1">
                    {onDispatchSpy && (
                      <button
                        type="button"
                        data-testid={`spy-dispatch-${spy.unitId}-${target.cityId}`}
                        {...appDom({
                          id: `spy-dispatch-${spy.unitId}-${target.cityId}`,
                          type: 'Button',
                          role: 'button',
                          scope: 'section',
                        })}
                        onClick={() => onDispatchSpy(spy.unitId, target.cityId)}
                        className="shrink-0 rounded border border-border/60 px-2 py-0.5 text-[10px] hover:bg-muted/60"
                      >
                        {t('espionage.dispatch')}
                      </button>
                    )}
                    {espionage?.ops && opsActions && (
                      <>
                        <SabotageControls
                          ops={espionage.ops}
                          spy={spy}
                          target={target}
                          players={players}
                          localPlayerIndex={localPlayerIndex}
                          onSabotage={opsActions.sabotageCity}
                        />
                        <PoisonControls
                          ops={espionage.ops}
                          spy={spy}
                          target={target}
                          players={players}
                          localPlayerIndex={localPlayerIndex}
                          onPoison={opsActions.poisonSupply}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )
      )}
    </div>
  );
}
